import { API_BASE_URL } from '../Utils/constants';
import { get, post, del } from './httpClient';
import { authFetch } from './authFetch';
import { spaceOwnerQueryParams } from './spaceMembersService';
import {
  putUserLotAttachmentToS3,
  normalizeUserLotAttachmentFileName,
} from './userLotsService';

// El Bearer (renovado, sin `Bearer null`, reintento ante 401) lo pone `authFetch`.
const getHeaders = () => ({
  'Content-Type': 'application/json',
});

const parseJsonResponse = async (response) => {
  let json = null;
  try {
    json = await response.json();
  } catch {
    json = {};
  }
  return {
    ...json,
    _httpStatus: response.status,
    _httpOk: response.ok,
  };
};

/**
 * Lee metadata, fotos, videos, adjuntos y contadores del contenido privado del lote.
 * Compat: `getLotUserContent(lotId, signal)`.
 * @param {string} lotId - UUID catastral del lote
 * @param {AbortSignal|{ signal?: AbortSignal, ownerUserId?: string|null }} [signalOrOptions]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1; ver espacios-invite).
 */
export const getLotUserContent = async (lotId, signalOrOptions) => {
  const isOptions = Boolean(signalOrOptions)
    && typeof signalOrOptions === 'object'
    && typeof signalOrOptions.aborted !== 'boolean';
  const signal = isOptions ? signalOrOptions.signal : signalOrOptions;
  const ownerUserId = isOptions ? signalOrOptions.ownerUserId : null;
  const params = new URLSearchParams({
    lot_id: lotId,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/getlotusercontent?${params}`, {
    method: 'GET',
    headers: getHeaders(),
    signal,
  });
  return parseJsonResponse(response);
};

/**
 * Crea o actualiza metadata del lote (campos Fase 1 + toggles de share).
 * @param {object} payload - ver buildLotUserContentUpsertPayload
 * @param {{ ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `owner_user_id` en el body (F2): la API aplica reglas
 *        de editor (solo copy comercial del lote del owner).
 */
export const upsertLotUserContent = async (payload, { ownerUserId } = {}) => {
  const response = await authFetch(`${API_BASE_URL}/upsertlotusercontent`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify({ ...payload, ...spaceOwnerQueryParams(ownerUserId) }),
  });
  return parseJsonResponse(response);
};

/**
 * URL presignada S3 para subir una foto del lote.
 * @param {string} lotId - UUID catastral del lote
 * @param {string} contentType - ej. image/jpeg
 * @param {{ ownerUserId?: string|null }} [options] `?owner_user_id=` (F2; mismo owner en el save)
 */
export const getLotContentPresignedUrl = async (lotId, contentType, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    lot_id: lotId,
    contentType,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/getlotcontentpresignedurl?${params}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * Persiste la foto en BD tras el upload a S3.
 * @param {string} lotId - UUID catastral del lote
 * @param {string} imageUrl - URL S3 devuelta por getLotContentPresignedUrl
 * @param {{ ownerUserId?: string|null }} [options] `?owner_user_id=` (F2; mismo owner que el presign)
 */
export const saveLotContentImage = async (lotId, imageUrl, { ownerUserId } = {}) => {
  const params = new URLSearchParams({
    lot_id: lotId,
    image_url: imageUrl,
    ...spaceOwnerQueryParams(ownerUserId),
  });
  const response = await authFetch(`${API_BASE_URL}/savelotcontentimage?${params}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/** @param {string} imageId - UUID de la foto del lote */
export const deleteLotContentImage = async (imageId) => {
  const params = new URLSearchParams({ image_id: imageId });
  const response = await authFetch(`${API_BASE_URL}/deletelotcontentimage?${params}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/** Marca una foto de contenido de lote como portada (`is_cover` en DB). */
export const setLotContentCoverImage = async (imageId, lotId) => {
  const params = new URLSearchParams({ image_id: imageId, lot_id: lotId });
  const response = await authFetch(`${API_BASE_URL}/setlotcontentimagecover?${params}`, {
    method: 'PUT',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * Elimina todo el contenido privado del lote en una sola operación:
 * metadata, fotos, adjuntos e ítems item_type=lot en listas.
 * @param {string} lotId - UUID catastral del lote
 */
export const deleteLotUserContent = async (lotId) => {
  const params = new URLSearchParams({ lot_id: lotId });
  const response = await authFetch(`${API_BASE_URL}/deletelotusercontent?${params}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return parseJsonResponse(response);
};

/**
 * Agrega un video de YouTube al contenido de lote (máx 4). Separado de fotos y de UF.
 * @param {string} lotId - UUID catastral del lote
 * @param {string} youtubeUrl - URL de YouTube (watch, shorts o youtu.be)
 * @param {{ ownerUserId?: string|null }} [options] `owner_user_id` en el body (F2)
 * @returns {Promise<object>} Envelope httpClient; el video creado viene en `data`.
 */
export const addLotContentVideo = (lotId, youtubeUrl, { ownerUserId } = {}) => post('/addlotcontentvideo', {
  body: {
    lot_id: lotId,
    youtube_url: youtubeUrl,
    ...spaceOwnerQueryParams(ownerUserId),
  },
});

/**
 * Lista videos de YouTube del contenido de lote.
 * @param {string} lotId - UUID catastral del lote
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1).
 */
export const getLotContentVideos = (lotId, { signal, ownerUserId } = {}) => get('/getlotcontentvideos', {
  params: { lot_id: lotId, ...spaceOwnerQueryParams(ownerUserId) },
  signal,
});

/**
 * Elimina un video de YouTube del contenido de lote.
 * @param {string} videoId - UUID del video
 * @returns {Promise<object>} Envelope; `data.videos` trae los restantes compactados.
 */
export const deleteLotContentVideo = (videoId) => del('/deletelotcontentvideo', {
  params: { video_id: videoId },
});

/**
 * GET /getlotcontentattachmentpresignedurl — ContentType + ContentLength firmados.
 * Separado de fotos (`getLotContentPresignedUrl`) y de adjuntos UF.
 * @param {string} lotId
 * @param {{ contentType: string, contentLength: number, fileName?: string }} params
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (F2; mismo owner en el save)
 */
export const getLotContentAttachmentPresignedUrl = (
  lotId,
  { contentType, contentLength, fileName, file_name: fileNameSnake } = {},
  { signal, ownerUserId } = {},
) => {
  const normalizedName = normalizeUserLotAttachmentFileName(fileName ?? fileNameSnake);
  return get('/getlotcontentattachmentpresignedurl', {
    params: {
      lot_id: lotId,
      contentType,
      contentLength,
      ...(normalizedName ? { file_name: normalizedName } : {}),
      ...spaceOwnerQueryParams(ownerUserId),
    },
    signal,
  });
};

/**
 * POST /savelotcontentattachment — cita s3_key + content_type tras el PUT a S3.
 * @param {string} lotId
 * @param {{ s3Key: string, contentType: string, fileName?: string }} params
 * @param {{ ownerUserId?: string|null }} [options] `owner_user_id` en el body (F2)
 */
export const saveLotContentAttachment = (
  lotId,
  { s3Key, contentType, fileName, file_name: fileNameSnake, original_filename: originalFilename } = {},
  { ownerUserId } = {},
) => post('/savelotcontentattachment', {
  body: {
    lot_id: lotId,
    s3_key: s3Key,
    content_type: contentType,
    file_name: normalizeUserLotAttachmentFileName(fileName ?? fileNameSnake ?? originalFilename),
    ...spaceOwnerQueryParams(ownerUserId),
  },
});

/**
 * GET /getlotcontentattachments?lot_id=
 * @param {string} lotId
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null }} [options]
 *        `ownerUserId` → `?owner_user_id=` (espacio ajeno, F1).
 */
export const getLotContentAttachments = (lotId, { signal, ownerUserId } = {}) => get('/getlotcontentattachments', {
  params: { lot_id: lotId, ...spaceOwnerQueryParams(ownerUserId) },
  signal,
});

/**
 * DELETE /deletelotcontentattachment?attachment_id=
 * @param {string} attachmentId
 * @returns {Promise<object>} Envelope; `data.attachments` trae los restantes compactados.
 */
export const deleteLotContentAttachment = (attachmentId) => del('/deletelotcontentattachment', {
  params: { attachment_id: attachmentId },
});

/**
 * Presign → PUT S3 → save. Devuelve el adjunto guardado (`data` del save).
 * Separado de fotos del lote y de adjuntos UF.
 * @param {string} lotId
 * @param {File|Blob} file
 * @param {{ signal?: AbortSignal, ownerUserId?: string|null, validate?: (file: File|Blob) => { ok: boolean, contentType?: string, message?: string } }} [options]
 *        `ownerUserId`: el mismo `owner_user_id` en presign y save (F2).
 */
export async function uploadLotContentAttachment(lotId, file, { signal, validate, ownerUserId } = {}) {
  const validation = typeof validate === 'function'
    ? validate(file)
    : { ok: Boolean(file?.type && file?.size), contentType: file?.type, message: 'Archivo inválido.' };
  if (!validation.ok) {
    throw new Error(validation.message || 'Archivo inválido.');
  }

  const contentType = validation.contentType || file.type;
  const contentLength = Number(file.size);
  const fileName = normalizeUserLotAttachmentFileName(file?.name);

  const presigned = await getLotContentAttachmentPresignedUrl(
    lotId,
    { contentType, contentLength, fileName },
    { signal, ownerUserId },
  );

  if (!presigned._httpOk || !presigned.data?.presignedUrl || !presigned.data?.s3Key) {
    const err = new Error(
      presigned?.message || presigned?.error || 'No se pudo preparar la subida del adjunto.',
    );
    err.apiResponse = presigned;
    throw err;
  }

  const {
    presignedUrl,
    s3Key,
    contentType: signedContentType,
  } = presigned.data;

  const putResult = await putUserLotAttachmentToS3(
    {
      presignedUrl,
      file,
      contentType: signedContentType || contentType,
    },
    { signal },
  );

  if (!putResult.ok) {
    throw new Error(putResult.message || 'No se pudo subir el archivo.');
  }

  const saveRes = await saveLotContentAttachment(lotId, {
    s3Key,
    contentType: signedContentType || contentType,
    fileName: fileName || file?.name,
  }, { ownerUserId });

  if (!saveRes._httpOk || !saveRes.data) {
    const err = new Error(
      saveRes?.message || saveRes?.error || 'No se pudo guardar el adjunto.',
    );
    err.apiResponse = saveRes;
    throw err;
  }

  return saveRes;
}

